import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { useAuth } from '@/lib/auth/AuthContext';
import { useAnalyticsApi } from '@/lib/useAnalytics';
import { tresciApi, type PozycjaKatalogu, type UczenKatalogu } from '@/lib/api/tresci';
import { komunikatBledu, type WartoscOdbiorcow } from '@/lib/tresci/tresciUi';
import type { Zakres } from './KreatorOdbiorcow';

/**
 * WYBÓR ODBIORCÓW — po zakresie (OWNER 2026-10-02).
 *
 * Miasta i szkoły przychodzą z katalogu SYSTEMU GŁÓWNEGO (nic nie jest wpisane w panel). Uczniów
 * wskazuje się WYSZUKIWARKĄ — panel nie przyjmuje wklejanych identyfikatorów, a wynik pokazuje
 * minimum potrzebne do rozpoznania osoby (imię + inicjał, zamaskowany adres, szkoła).
 */
export interface WyborOdbiorcowProps {
  zakres: Zakres;
  wartosc: WartoscOdbiorcow;
  onZmiana: (w: WartoscOdbiorcow) => void;
  wylaczone?: boolean;
  /**
   * „Konkretne osoby” z PEŁNYMI danymi (OWNER 17.10, skórki): imię i nazwisko + pełny e-mail z wyszukiwarki
   * Administracji → Użytkownicy (fraza w treści POST, zdolność `users.read`). Bez tej zdolności — zwykła
   * wyszukiwarka katalogu (zamaskowana). Pasek zostaje przy zamaskowanej.
   */
  pelneDaneOsob?: boolean;
}

/** Etykieta osoby: 1. linia imię i nazwisko (albo jawny brak), 2. linia pełny e-mail — nigdy szkoła zamiast osoby. */
export function EtykietaOsoby({ u }: { u: UczenKatalogu }) {
  const nazwa = (u.fullName ?? '').trim();
  return (
    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', py: 0.5, lineHeight: 1.3, textAlign: 'left' }}>
      <Box component="span" sx={{ fontWeight: 700 }}>{nazwa || 'brak imienia i nazwiska'}</Box>
      <Box component="span" sx={{ wordBreak: 'break-all' }}>{u.email ?? u.maskedEmail ?? '—'}</Box>
    </Box>
  );
}

/** Chip z dwiema liniami — pełny e-mail czytelny, bez obcinania. */
const CHIP_DWULINIOWY = { height: 'auto', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', display: 'block' } } as const;

export function WyborOdbiorcow({ zakres, wartosc, onZmiana, wylaczone = false, pelneDaneOsob = false }: WyborOdbiorcowProps) {
  const { api } = useServices();
  const auth = useAuth();
  const administracja = useAnalyticsApi();
  const pelne = pelneDaneOsob && auth.has('users.read');
  const [miasta, setMiasta] = useState<PozycjaKatalogu[]>([]);
  const [blad, setBlad] = useState<string | null>(null);
  const [miastoSzkol, setMiastoSzkol] = useState<PozycjaKatalogu | null>(null);
  const [szkolyMiasta, setSzkolyMiasta] = useState<PozycjaKatalogu[]>([]);
  const [fraza, setFraza] = useState('');
  const [wyniki, setWyniki] = useState<UczenKatalogu[]>([]);
  const [szuka, setSzuka] = useState(false);

  useEffect(() => {
    if (zakres === 'GLOBAL') return;
    let aktualne = true;
    // Lista miast = DOKŁADNIE odpowiedź katalogu. Bez listy zastępczej: gdy katalog milczy,
    // operator widzi powód i pustą listę, a nie miasta wpisane w panel.
    tresciApi.miasta(api)
      .then((m) => { if (aktualne) { setMiasta(m); setBlad(null); } })
      .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, zakres]);

  useEffect(() => {
    if (!miastoSzkol) return;
    let aktualne = true;
    tresciApi.szkoly(api, miastoSzkol.id)
      .then((s) => { if (aktualne) { setSzkolyMiasta(s); setBlad(null); } })
      .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, miastoSzkol]);

  useEffect(() => {
    const q = fraza.trim();
    if (q.length < 3) return;
    let aktualne = true;
    const t = setTimeout(() => {
      setSzuka(true);
      (pelne
        ? administracja.uzytkownicy({ q, rola: 'Klient', rozmiar: 25 }).then((o) => o.konta.map((k): UczenKatalogu => ({
          userId: k.id, displayName: k.imie ?? '', maskedEmail: null, schoolName: k.szkola, cityName: k.miasto,
          fullName: k.imie, email: k.email,
        })))
        : tresciApi.uczniowie(api, q))
        .then((w) => { if (aktualne) { setWyniki(w); setBlad(null); } })
        .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); })
        .finally(() => { if (aktualne) setSzuka(false); });
    }, 300);
    // Starsza odpowiedź (wolniejsza sieć) nie może nadpisać wyników nowszej frazy.
    return () => { aktualne = false; clearTimeout(t); };
  }, [api, administracja, pelne, fraza]);

  if (zakres === 'GLOBAL') return null;
  // Puste listy wyprowadzone z warunku, a nie ustawiane w efekcie (bez kaskady renderów).
  const szkolyWidoczne = miastoSzkol ? szkolyMiasta : [];
  const wynikiWidoczne = fraza.trim().length >= 3 ? wyniki : [];

  return (
    <Box data-testid="wybor-odbiorcow" sx={{ mt: 2 }}>
      {blad ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>{blad}</Alert> : null}

      {zakres === 'CITY' ? (
        <Autocomplete
          multiple
          options={miasta}
          value={wartosc.miasta}
          disabled={wylaczone}
          getOptionLabel={(m) => m.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, v) => onZmiana({ ...wartosc, miasta: v })}
          renderInput={(p) => <TextField {...p} label="Miasta" />}
        />
      ) : null}

      {zakres === 'SCHOOL' ? (
        <Stack spacing={1}>
          <Autocomplete
            options={miasta}
            value={miastoSzkol}
            disabled={wylaczone}
            getOptionLabel={(m) => m.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setMiastoSzkol(v)}
            renderInput={(p) => <TextField {...p} label="Miasto (zawęża listę szkół)" />}
          />
          <Autocomplete
            multiple
            options={[...wartosc.szkoly, ...szkolyWidoczne.filter((s) => !wartosc.szkoly.some((w) => w.id === s.id))]}
            value={wartosc.szkoly}
            disabled={wylaczone}
            getOptionLabel={(s) => s.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => onZmiana({ ...wartosc, szkoly: v })}
            renderInput={(p) => <TextField {...p} label="Szkoły" />}
          />
        </Stack>
      ) : null}

      {zakres === 'USER' ? (
        <Stack spacing={1}>
          <TextField
            label="Szukaj ucznia (imię, nazwisko albo adres — min. 3 znaki)"
            value={fraza}
            disabled={wylaczone}
            onChange={(e) => setFraza(e.target.value)}
            slotProps={{ input: { endAdornment: szuka && fraza.trim().length >= 3 ? <CircularProgress size={16} /> : null } }}
          />
          {wynikiWidoczne.length > 0 ? (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} aria-label="Wyniki wyszukiwania">
              {wynikiWidoczne.map((u) => (
                <Chip
                  key={u.userId}
                  variant="outlined"
                  sx={pelne ? CHIP_DWULINIOWY : undefined}
                  label={pelne ? <EtykietaOsoby u={u} /> : `${u.displayName} · ${u.maskedEmail ?? '—'} · ${u.schoolName ?? 'bez szkoły'}`}
                  onClick={() => {
                    if (!wartosc.uczniowie.some((x) => x.userId === u.userId)) {
                      onZmiana({ ...wartosc, uczniowie: [...wartosc.uczniowie, u] });
                    }
                  }}
                />
              ))}
            </Stack>
          ) : null}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Wybrani:</Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} data-testid="wybrani-uczniowie">
            {wartosc.uczniowie.map((u) => (
              <Chip
                key={u.userId}
                color="primary"
                sx={pelne ? CHIP_DWULINIOWY : undefined}
                label={pelne ? <EtykietaOsoby u={u} /> : `${u.displayName} · ${u.schoolName ?? 'bez szkoły'}`}
                onDelete={wylaczone ? undefined : () => onZmiana({ ...wartosc, uczniowie: wartosc.uczniowie.filter((x) => x.userId !== u.userId) })}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Box>
  );
}
